import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, CalendarDays, ChevronDown, ChevronUp, GripVertical, Plus, X } from "lucide-react";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const LOCATION_PALETTE = [
  { bg: 'bg-cyan-500', light: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', dot: 'bg-cyan-400' },
  { bg: 'bg-purple-500', light: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-400' },
  { bg: 'bg-emerald-500', light: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-400' },
  { bg: 'bg-orange-500', light: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', dot: 'bg-orange-400' },
  { bg: 'bg-amber-500', light: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-400' },
  { bg: 'bg-indigo-500', light: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', dot: 'bg-indigo-400' },
];

const AdminTimelineEditor = () => {
  const [timelineMonths, setTimelineMonths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [autoDetect, setAutoDetect] = useState(true);
  const [manualOverride, setManualOverride] = useState(null);
  const [locations, setLocations] = useState([]);
  const [diveCenters, setDiveCenters] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchTimeline();
    fetchLocations();
    fetchDiveCenters();
  }, []);

  const fetchTimeline = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/timeline`);
      setTimelineMonths(res.data.months || []);
      setAutoDetect(res.data.auto_detect_current !== false);
      setManualOverride(res.data.manual_current_override || null);
    } catch (err) {
      console.error("Failed to load timeline:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations(res.data || []);
    } catch (err) {
      console.error("Failed to load locations:", err);
    }
  };

  const fetchDiveCenters = async () => {
    try {
      const res = await axios.get(`${API}/map/dive-centers`);
      setDiveCenters(res.data || []);
    } catch (err) {
      console.error("Failed to load dive centers:", err);
    }
  };

  const save = async () => {
    try {
      setLoading(true);
      setSaveMsg("");
      const res = await axios.put(`${API}/timeline`,
        { months: timelineMonths, auto_detect_current: autoDetect, manual_current_override: manualOverride },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.data?.months) setTimelineMonths(res.data.data.months);
      setSaveMsg("success");
      setTimeout(() => setSaveMsg(""), 4000);
    } catch (err) {
      console.error("Failed to save:", err);
      setSaveMsg("error");
    } finally {
      setLoading(false);
    }
  };

  const updateYear = (idx, year) => {
    const updated = [...timelineMonths];
    updated[idx] = { ...updated[idx], year: parseInt(year) };
    setTimelineMonths(updated);
  };

  const updateDay = (idx, field, value) => {
    const updated = [...timelineMonths];
    const numVal = value ? parseInt(value) : null;
    updated[idx] = { ...updated[idx], [field]: numVal };
    setTimelineMonths(updated);
  };

  const updateLocation = (idx, locationId) => {
    const updated = [...timelineMonths];
    updated[idx] = { ...updated[idx], location_id: locationId || null, dive_center_id: null };
    setTimelineMonths(updated);
  };

  const setCurrent = (idx) => {
    setAutoDetect(false);
    setManualOverride(idx);
    setTimelineMonths(timelineMonths.map((m, i) => ({ ...m, is_current: i === idx })));
  };

  const moveMonth = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= timelineMonths.length) return;
    const updated = [...timelineMonths];
    const [moved] = updated.splice(fromIdx, 1);
    updated.splice(toIdx, 0, moved);
    setTimelineMonths(updated);
  };

  const getLocColor = (locationId) => {
    if (!locationId) return null;
    const i = locations.findIndex(l => l.id === locationId);
    return i >= 0 ? LOCATION_PALETTE[i % LOCATION_PALETTE.length] : LOCATION_PALETTE[0];
  };

  const centersAtLocation = (locationId) => {
    if (!locationId) return [];
    return diveCenters.filter(c => c.location_id === locationId);
  };

  const splitMonth = (idx) => {
    const updated = [...timelineMonths];
    const original = updated[idx];
    // Calculate complementary day range
    const origEnd = original.end_day || 15;
    const newEntry = {
      month: original.month,
      year: original.year,
      is_current: false,
      order: original.order,
      location_id: null,
      dive_center_id: null,
      location_name: null,
      start_day: origEnd + 1,
      end_day: null, // null = end of month
    };
    // If original has no day range set, default it to first half
    if (!original.start_day && !original.end_day) {
      updated[idx] = { ...original, start_day: 1, end_day: 15 };
      newEntry.start_day = 16;
    }
    updated.splice(idx + 1, 0, newEntry);
    setTimelineMonths(updated);
  };

  const removeEntry = (idx) => {
    const updated = [...timelineMonths];
    updated.splice(idx, 1);
    setTimelineMonths(updated);
  };

  // Check if a row has a sibling entry for the same month+year
  const hasSibling = (idx) => {
    const m = timelineMonths[idx];
    return timelineMonths.some((other, i) => i !== idx && other.month === m.month && other.year === m.year);
  };

  if (loading && timelineMonths.length === 0) {
    return (
      <Card><CardContent className="py-12 text-center text-gray-500">Loading timeline...</CardContent></Card>
    );
  }

  return (
    <Card className="mb-12" data-testid="timeline-editor">
      <CardHeader className="pb-3">
        <CardTitle className="text-2xl">Travel Schedule</CardTitle>
        <p className="text-sm text-gray-500 mt-1">Assign a location to each month. Use the day fields for exact date ranges. Click <strong>+</strong> to split a month between two locations (e.g., 1-15 Panglao, 16-30 Greece).</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Auto-detect toggle */}
        <label className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200 cursor-pointer" data-testid="auto-detect-toggle">
          <input
            type="checkbox"
            className="w-4 h-4 rounded text-cyan-500 focus:ring-cyan-500"
            checked={autoDetect}
            onChange={(e) => setAutoDetect(e.target.checked)}
          />
          <div>
            <span className="text-sm font-medium text-slate-700">Auto-detect current month</span>
            <p className="text-xs text-slate-500">Automatically highlights the current month based on today's date</p>
          </div>
        </label>

        {/* Month rows */}
        <div className="space-y-1">
          {timelineMonths.map((month, idx) => {
            const color = getLocColor(month.location_id);
            const centers = centersAtLocation(month.location_id);
            const isCurrent = month.is_current;

            return (
              <div
                key={idx}
                className={`group flex flex-wrap items-center gap-2 sm:gap-3 px-3 py-2.5 rounded-lg border transition-all ${
                  isCurrent
                    ? 'bg-cyan-50 border-cyan-300 shadow-sm'
                    : color
                    ? `${color.light} ${color.border}`
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`timeline-row-${idx}`}
              >
                {/* Reorder buttons */}
                <div className="flex flex-col gap-0.5 opacity-40 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => moveMonth(idx, idx - 1)}
                    className="p-0.5 hover:bg-black/5 rounded disabled:opacity-20"
                    disabled={idx === 0}
                    data-testid={`timeline-move-up-${idx}`}
                  >
                    <ChevronUp className="w-3.5 h-3.5 text-slate-500" />
                  </button>
                  <button
                    onClick={() => moveMonth(idx, idx + 1)}
                    className="p-0.5 hover:bg-black/5 rounded disabled:opacity-20"
                    disabled={idx === timelineMonths.length - 1}
                    data-testid={`timeline-move-down-${idx}`}
                  >
                    <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                  </button>
                </div>

                {/* Color dot */}
                <div className={`w-3 h-3 rounded-full shrink-0 ${
                  color ? color.dot : 'bg-slate-300'
                } ${isCurrent ? 'ring-2 ring-cyan-400 ring-offset-1' : ''}`} />

                {/* Month name */}
                <span className={`w-12 text-sm font-bold tracking-wide ${
                  isCurrent ? 'text-cyan-800' : color ? color.text : 'text-slate-400'
                }`}>
                  {month.month}
                </span>

                {/* Year select */}
                <select
                  className="w-20 px-2 py-1.5 text-sm border border-slate-200 rounded-md bg-white focus:ring-1 focus:ring-cyan-400 focus:border-cyan-400"
                  value={month.year}
                  onChange={(e) => updateYear(idx, e.target.value)}
                  data-testid={`timeline-year-${idx}`}
                >
                  {[2024, 2025, 2026, 2027, 2028].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>

                {/* Day range inputs */}
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min="1"
                    max="31"
                    placeholder="1"
                    className="w-12 px-1.5 py-1.5 text-sm text-center border border-slate-200 rounded-md bg-white focus:ring-1 focus:ring-cyan-400 focus:border-cyan-400"
                    value={month.start_day || ""}
                    onChange={(e) => updateDay(idx, 'start_day', e.target.value)}
                    data-testid={`timeline-start-day-${idx}`}
                  />
                  <span className="text-xs text-slate-400">-</span>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    placeholder="31"
                    className="w-12 px-1.5 py-1.5 text-sm text-center border border-slate-200 rounded-md bg-white focus:ring-1 focus:ring-cyan-400 focus:border-cyan-400"
                    value={month.end_day || ""}
                    onChange={(e) => updateDay(idx, 'end_day', e.target.value)}
                    data-testid={`timeline-end-day-${idx}`}
                  />
                </div>

                {/* Location select */}
                <div className="flex-1 min-w-[150px] flex items-center gap-2">
                  <MapPin className={`w-4 h-4 shrink-0 ${color ? color.text : 'text-slate-300'}`} />
                  <select
                    className={`flex-1 px-2 py-1.5 text-sm border rounded-md bg-white focus:ring-1 focus:ring-cyan-400 focus:border-cyan-400 ${
                      month.location_id ? 'border-slate-200 text-slate-800' : 'border-dashed border-slate-300 text-slate-400'
                    }`}
                    value={month.location_id || ""}
                    onChange={(e) => updateLocation(idx, e.target.value)}
                    data-testid={`timeline-location-${idx}`}
                  >
                    <option value="">No location</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}, {loc.country}
                      </option>
                    ))}
                  </select>

                  {/* Dive center count badge */}
                  {centers.length > 0 && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${
                      color ? `${color.light} ${color.text} ${color.border} border` : 'bg-slate-100 text-slate-600'
                    }`} data-testid={`timeline-centers-count-${idx}`}>
                      {centers.length} {centers.length === 1 ? 'center' : 'centers'}
                    </span>
                  )}
                </div>

                {/* Current badge / set current button */}
                {isCurrent ? (
                  <span className="text-xs font-bold text-cyan-600 bg-cyan-100 px-2.5 py-1 rounded-full shrink-0" data-testid={`timeline-current-badge-${idx}`}>
                    NOW
                  </span>
                ) : (
                  <button
                    onClick={() => setCurrent(idx)}
                    className="text-xs text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 px-2 py-1 rounded-full transition-colors shrink-0"
                    data-testid={`timeline-set-current-${idx}`}
                  >
                    Set current
                  </button>
                )}

                {/* Split month / Remove entry buttons */}
                <div className="flex items-center gap-1 shrink-0">
                  {!hasSibling(idx) && (
                    <button
                      onClick={() => splitMonth(idx)}
                      className="p-1 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-colors"
                      title="Split month (add second location)"
                      data-testid={`timeline-split-${idx}`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {hasSibling(idx) && (
                    <button
                      onClick={() => removeEntry(idx)}
                      className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      title="Remove this entry"
                      data-testid={`timeline-remove-${idx}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Location legend */}
        {locations.length > 0 && (
          <div className="pt-4 border-t border-slate-100">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Locations & Dive Centers</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {locations.map((loc, i) => {
                const c = LOCATION_PALETTE[i % LOCATION_PALETTE.length];
                const centers = centersAtLocation(loc.id);
                return (
                  <div key={loc.id} className={`flex items-start gap-2 p-2 rounded-md ${c.light} ${c.border} border`}>
                    <div className={`w-2.5 h-2.5 rounded-full mt-0.5 shrink-0 ${c.dot}`} />
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${c.text} truncate`}>{loc.name}, {loc.country}</p>
                      {centers.length > 0 && (
                        <p className="text-xs text-slate-500 truncate">
                          {centers.map(dc => dc.name).join(' · ')}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Save bar */}
        <div className="flex justify-between items-center pt-4 border-t border-slate-100">
          <p className="text-xs text-slate-500">
            {saveMsg === 'success' && <span className="text-emerald-600 font-medium">Saved successfully</span>}
            {saveMsg === 'error' && <span className="text-red-600 font-medium">Failed to save. Try again.</span>}
            {!saveMsg && 'Changes update the Home and Map pages'}
          </p>
          <Button
            className="bg-cyan-500 hover:bg-cyan-600"
            onClick={save}
            disabled={loading}
            data-testid="timeline-save-btn"
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminTimelineEditor;
